// region imports
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import shallowEqual from 'recompose/shallowEqual'

import InputGroup from '@/ui/Input/InputGroup'
import InputGroupAddon from '@/ui/Input/InputGroupAddon'
import FormFeedback from '@/ui/Form/FormFeedback'
import FormGroup from '@/ui/Form/FormGroup'
import FormText from '@/ui/Form/FormText'
import Input from '@/ui/Input/Input'
import Textarea from '@/ui/Input/Textarea'
import Label from '@/ui/Input/Label'
// endregion

/**
 * Check if a value is valid to be displayed inside an input.
 *
 * @param The value to check.
 * @returns True if the string provided is valid, false otherwise.
 */
function isValid(value) {
  return (
    value !== '' &&
    value !== undefined &&
    value !== null &&
    !(Array.isArray(value) && value.length === 0)
  )
}

function has(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop)
}

class InputField extends Component {
  static propTypes = {
    className: PropTypes.string,
    /**
     * The text string to use for the default value.
     */
    defaultValue: PropTypes.any,
    /**
     * Disables the text field if set to true.
     */
    disabled: PropTypes.bool,
    /**
     * The error content to display.
     */
    errorText: PropTypes.node,
    /**
     * If true, the field receives the property width 100%.
     */
    fullWidth: PropTypes.bool,
    /**
     * The hint content to display.
     */
    hintText: PropTypes.node,
    /**
     * The content to use for the label element.
     */
    labelText: PropTypes.node,
    /**
     * The id prop for the text field.
     */
    id: PropTypes.string,
    /**
     * If true, a textarea element will be rendered.
     * The textarea also grows and shrinks according to the number of lines.
     */
    multiLine: PropTypes.bool,
    /**
     * Name applied to the input.
     */
    name: PropTypes.string,
    /** @ignore */
    onBlur: PropTypes.func,
    /**
     * Callback function that is fired when the InputField's value changes.
     *
     * @param {object} event Change event targeting the text field.
     * @param {string} newValue The new value of the text field.
     */
    onChange: PropTypes.func,
    /** @ignore */
    onFocus: PropTypes.func,
    /**
     * Number of rows to display when multiLine option is set to true.
     */
    rows: PropTypes.number,
    /**
     * Maximum number of rows to display when
     * multiLine option is set to true.
     */
    maxRows: PropTypes.number,
    /**
     * Specifies the type of input to display
     * such as "password" or "text".
     */
    type: PropTypes.string,
    /**
     * The value of the text field.
     */
    value: PropTypes.any,
    /**
     * Addon icon
     */
    icon: PropTypes.any,
    /**
     * Display the addon on right side
     */
    addonRight: PropTypes.bool,
  }

  static defaultProps = {
    disabled: false,
    multiLine: false,
    fullWidth: false,
    addonRight: false,
    type: 'text',
    rows: 1,
  }

  state = {
    isFocused: false,
    errorText: undefined,
    hasValue: false,
  }

  componentWillMount() {
    const { name, hintText, labelText, id } = this.props // eslint-disable-line no-unused-vars

    this.setState({
      errorText: this.props.errorText,
      hasValue: isValid(this.props.value) || isValid(this.props.defaultValue),
    })
    this.uniqueId = `${name || 'input'}-${new Date().getTime()}`
    // const uniqueId = `${name}-${hintText}-${labelText}-${Math.floor(Math.random() * 0xffff)}`
    // this.uniqueId = uniqueId.replace(/[^A-Za-z0-9-]/gi, '')
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.errorText !== this.props.errorText) {
      this.setState({
        errorText: nextProps.errorText,
      })
    }

    if (has(nextProps, 'value')) {
      const hasValue = isValid(nextProps.value)

      this.setState({
        hasValue,
      })
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    return (
      !shallowEqual(this.props, nextProps) ||
      !shallowEqual(this.state, nextState) ||
      !shallowEqual(this.context, nextContext)
    )
  }

  getValue() {
    return this.input ? this.input.value : undefined
  }

  select() {
    if (this.input) {
      this.input.select()
    }
  }

  focus() {
    if (this.input) {
      this.input.focus()
    }
  }

  blur() {
    if (this.input) {
      this.input.blur()
    }
  }

  handleInputBlur = event => {
    this.setState({ isFocused: false })
    if (this.props.onBlur) {
      this.props.onBlur(event)
    }
  }

  handleInputChange = event => {
    if (!has(this.props, 'value')) {
      this.setState({ hasValue: isValid(event.target.value) })
    }
    if (this.props.onChange) {
      this.props.onChange(event, event.target.value)
    }
  }

  handleInputFocus = event => {
    if (this.props.disabled) {
      return
    }
    this.setState({ isFocused: true })
    if (this.props.onFocus) {
      this.props.onFocus(event)
    }
  }

  // handleHeightChange = (event, height) => {
  //   const newHeight = height + 24
  //   this.input.style.height = `${newHeight}px`
  // }

  _isControlled() {
    return has(this.props, 'value')
  }

  render() {
    const {
      className,
      disabled,
      errorText,
      fullWidth,
      hintText,
      labelText,
      id,
      multiLine,
      onBlur,
      onChange,
      onFocus,
      type,
      icon: Icon,
      addonRight,
      rows,
      maxRows,
      ...other
    } = this.props

    const inputId = id || this.uniqueId

    const inputProps = {
      id: inputId,
      ref: elem => {
        this.input = elem
      },
      disabled: this.props.disabled,
      onBlur: this.handleInputBlur,
      onChange: this.handleInputChange,
      onFocus: this.handleInputFocus,
      ...(className && { className }),
    }
    // onHeightChange={this.handleHeightChange}

    const InputElement = (
      <Choose>
        <When condition={multiLine}>
          <Textarea rows={rows} maxRows={maxRows} {...other} {...inputProps} />
        </When>
        <Otherwise>
          <Input type={type} {...other} {...inputProps} />
        </Otherwise>
      </Choose>
    )

    const formProps = this.state.errorText ? { color: 'danger' } : {}

    return (
      <FormGroup {...formProps} className={className}>
        <If condition={labelText}>
          <Label key="label" htmlFor={this.props.id}>
            {labelText}
          </Label>
        </If>
        <Choose>
          <When condition={Icon}>
            <InputGroup>
              <InputGroupAddon>
                <Icon />
              </InputGroupAddon>
              {InputElement}
            </InputGroup>
          </When>
          <Otherwise>{InputElement}</Otherwise>
        </Choose>
        <If condition={this.state.errorText}>
          <FormFeedback>{this.state.errorText}</FormFeedback>
        </If>
        <If condition={hintText}>
          <FormText>{hintText}</FormText>
        </If>
      </FormGroup>
    )
  }
}

export default InputField



// WEBPACK FOOTER //
// ./ui/Input/InputField.js